import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { SlidersHorizontal, MapPin, GraduationCap, X } from "lucide-react";

const FALLBACK_GRADES = ["Nursery","LKG","UKG","G01","G02","G03","G04","G05","G06","G07","G08","G09","G10","G11","G12"];

export default function DiscoveryFilterBar({ value, onChange, theme = "light" }) {
  const dark = theme === "dark";
  const [open, setOpen] = useState(false);
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [boards, setBoards] = useState([]);
  const [grades, setGrades] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const v = value;

  useEffect(() => {
    api.get("/geo/countries").then(r => setCountries(r.data)).catch(() => {});
    api.get("/taxonomy/boards").then(r => setBoards(r.data)).catch(() => {});
    api.get("/taxonomy/subjects").then(r => setSubjects(r.data)).catch(() => {});
  }, []);
  useEffect(() => {
    setStates([]); setDistricts([]);
    if (v.country) api.get("/geo/states", { params: { country_code: v.country } }).then(r => setStates(r.data)).catch(() => {});
  }, [v.country]);
  useEffect(() => {
    setDistricts([]);
    if (v.country && v.state) api.get("/geo/districts", { params: { country_code: v.country, state: v.state } }).then(r => setDistricts(r.data)).catch(() => {});
  }, [v.state]);
  useEffect(() => {
    api.get("/taxonomy/grades", { params: { board_code: v.board || undefined } })
      .then(r => setGrades(r.data && r.data.length ? r.data : FALLBACK_GRADES)).catch(() => setGrades(FALLBACK_GRADES));
  }, [v.board]);

  const set = (patch) => onChange({ ...v, ...patch });
  const selCls = dark
    ? "bg-white/10 border-2 border-white/25 text-white text-xs rounded-md px-2 py-1.5 focus:border-white/60 outline-none [&>option]:text-black"
    : "bg-white border-2 border-black text-xs rounded-md px-2 py-1.5 shadow-[2px_2px_0_0_#000] outline-none";
  const activeCount = ["country","state","district","board","grade","subject"].filter(k => v[k]).length;
  const clearAll = () => onChange({ country: "", state: "", district: "", board: "", grade: "", subject: "" });

  const Selects = (
    <div className="flex flex-wrap items-center gap-1.5">
      <span className={`flex items-center gap-1 text-[11px] font-bold uppercase tracking-wide ${dark ? "text-white/50" : "text-black/50"}`}><MapPin size={12} /> Location</span>
      <select className={selCls} value={v.country} onChange={e => set({ country: e.target.value, state: "", district: "" })} data-testid="df-country">
        <option value="">Country</option>
        {countries.map(c => <option key={c.country_code} value={c.country_code}>{c.country_name}</option>)}
      </select>
      <select className={selCls} value={v.state} onChange={e => set({ state: e.target.value, district: "" })} disabled={!v.country} data-testid="df-state">
        <option value="">State</option>
        {states.map(s => <option key={s} value={s}>{s}</option>)}
      </select>
      <select className={selCls} value={v.district} onChange={e => set({ district: e.target.value })} disabled={!v.state} data-testid="df-district">
        <option value="">District</option>
        {districts.map(d => <option key={d} value={d}>{d}</option>)}
      </select>
      <span className={`ml-1 flex items-center gap-1 text-[11px] font-bold uppercase tracking-wide ${dark ? "text-white/50" : "text-black/50"}`}><GraduationCap size={12} /> Academic</span>
      <select className={selCls} value={v.board} onChange={e => set({ board: e.target.value, grade: "" })} data-testid="df-board">
        <option value="">Board</option>
        {boards.map(b => <option key={b.code} value={b.code}>{b.code}</option>)}
      </select>
      <select className={selCls} value={v.grade} onChange={e => set({ grade: e.target.value })} data-testid="df-grade">
        <option value="">Grade</option>
        {grades.map(g => <option key={g} value={g}>{g}</option>)}
      </select>
      <select className={selCls} value={v.subject} onChange={e => set({ subject: e.target.value })} data-testid="df-subject">
        <option value="">Subject</option>
        {subjects.map(s => <option key={s.subject_id} value={s.name}>{s.name}</option>)}
      </select>
      {activeCount > 0 && (
        <button onClick={clearAll} className={`flex items-center gap-1 text-[11px] font-bold px-2 py-1 rounded-md ${dark ? "text-white/70 hover:text-white" : "text-black/60 hover:text-black"}`} data-testid="df-clear">
          <X size={12} /> Clear
        </button>
      )}
    </div>
  );

  return (
    <div className={`sticky top-[53px] z-20 border-b-2 ${dark ? "border-white/15 bg-[#0F1020]/95 backdrop-blur" : "border-black bg-[#FAF7F0]/95 backdrop-blur"}`} data-testid="discovery-filter-bar">
      <div className="max-w-6xl mx-auto px-4 py-2">
        {/* Mobile toggle */}
        <div className="sm:hidden flex items-center justify-between">
          <button onClick={() => setOpen(o => !o)} className={`flex items-center gap-2 text-sm font-bold px-3 py-1.5 rounded-full border-2 ${dark ? "border-white/40 text-white" : "border-black bg-white shadow-[2px_2px_0_0_#000]"}`} data-testid="df-mobile-toggle">
            <SlidersHorizontal size={15} /> Filters {activeCount > 0 && <span className={`nb-badge ${dark ? "bg-white text-black" : "bg-[#FDE047]"}`}>{activeCount}</span>}
          </button>
        </div>
        {/* Desktop: single slim row */}
        <div className="hidden sm:block">{Selects}</div>
        {/* Mobile: collapsible */}
        {open && <div className="sm:hidden mt-2">{Selects}</div>}
      </div>
    </div>
  );
}
